import {useState} from 'react';

/**
 * Manages form validation state, dirty tracking, and touched state.
 *
 * Dirty tracking mirrors Angular's FormControl behaviour: a field is marked dirty the moment
 * `markAsDirty(fieldName)` is called for it — no value comparison is performed. The dirty flag
 * is a one-way latch that only resets on an explicit `reset()` call.
 *
 * @param {Function|null|undefined} validate - Registry validate function; receives state and returns {valid, fields}.
 *   Must be a stable reference (e.g. from a module-level registry constant) to avoid unnecessary recomputation.
 * @param {Object} state - Current form values
 * @returns {[Object, {markAsDirty: Function, markAllAsTouched: Function, reset: Function}]}
 */
const useFormState = (validate, state) => {
  const [allTouched, setAllTouched] = useState(false);
  const [dirtyFields, setDirtyFields] = useState({});

  /** @param {string} fieldName */
  const markAsDirty = (fieldName) => {
    setDirtyFields((dirty) => (dirty[fieldName] ? dirty : {...dirty, [fieldName]: true}));
  };

  const markAllAsTouched = () => setAllTouched(true);

  const reset = () => {
    setAllTouched(false);
    setDirtyFields({});
  };

  if (!validate) return [{valid: false, dirty: false, fields: {}}, {markAsDirty, markAllAsTouched, reset}];

  const {valid, fields} = validate(state);
  const fieldEntries = Object.keys(fields).map((key) => [
    key,
    {
      errors: fields[key].errors,
      valid: !fields[key].errors,
      dirty: allTouched || !!dirtyFields[key],
    },
  ]);
  const formState = {
    valid,
    dirty: allTouched || fieldEntries.some(([, f]) => f.dirty),
    fields: Object.fromEntries(fieldEntries),
  };

  return [formState, {markAsDirty, markAllAsTouched, reset}];
};

export default useFormState;
